import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus, ArrowLeft, Save, MapPin, Hotel, Plane, Landmark, Star, ArrowUp, ArrowDown, X, Search, Info,
} from 'lucide-react';
import { confirm } from '../components/ui/confirm-dialog';
import { useToast } from '../components/ui/use-toast';
import { useRestaurant } from '../context/RestaurantContext';
import { useT } from '../context/LanguageContext';
import { finderPlacesService } from '../services/finderPlacesService';
import { useModuleAccess } from '../utils/useModuleAccess';
import AccessDenied from '../components/AccessDenied';

/* ------------------------------- small inputs ------------------------------ */

const inputCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring';

function Field({ label, required, children, hint }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">
        {label}{required && <span className="text-destructive"> *</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

const TYPE_ICON = { hotel: Hotel, airport: Plane, landmark: Landmark, town: MapPin, village: MapPin };

function KindIcon({ kind, className = 'h-4 w-4' }) {
  const Icon = TYPE_ICON[kind] || MapPin;
  return <Icon className={className} aria-hidden="true" />;
}

const EMPTY_LANDMARK = {
  name: '',
  type: 'hotel',
  district_id: '',
  lat: '',
  lng: '',
  aliases: '',
  is_active: true,
};

const fromLandmark = (lm) => ({
  id: lm.id,
  name: lm.name || '',
  type: lm.type || 'hotel',
  district_id: lm.district_id ?? '',
  lat: lm.lat ?? '',
  lng: lm.lng ?? '',
  aliases: (lm.aliases || []).join(', '),
  is_active: lm.is_active !== false,
});

/* --------------------------------- page ---------------------------------- */

// `embedded` renders this as the Places section inside Website Settings, which already provides the page chrome.
export default function FinderPlacesManagement({ embedded = false }) {
  const { t } = useT();
  const { restaurantId } = useRestaurant();
  const { canView, canEdit, canCreate } = useModuleAccess('settings');
  const [tab, setTab] = useState('landmarks');
  const [landmarks, setLandmarks] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [filter, setFilter] = useState('');
  // null = list; object = editor (id null = new)
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = async () => {
    const { landmarks: rows, districts: ds } = await finderPlacesService.list(restaurantId, { includeInactive: showInactive });
    setLandmarks(rows);
    setDistricts(ds);
  };

  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    setLoading(true);
    finderPlacesService.list(restaurantId, { includeInactive: showInactive })
      .then(({ landmarks: rows, districts: ds }) => {
        if (cancelled) return;
        setLandmarks(rows);
        setDistricts(ds);
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.error || e.message || t('settings.places.loadFailed'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, showInactive]);

  const openNew = () => { setEditing({ id: null, ...EMPTY_LANDMARK }); setSaveError(null); };
  const openExisting = (lm) => { setEditing(fromLandmark(lm)); setSaveError(null); };
  const closeEditor = () => { setEditing(null); setSaveError(null); };
  const setField = (k, v) => setEditing((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!editing.name.trim()) { setSaveError(t('settings.places.validation.name')); return; }
    if (!editing.district_id) { setSaveError(t('settings.places.validation.district')); return; }
    if (editing.lat === '' || editing.lng === '') { setSaveError(t('settings.places.validation.coords')); return; }
    setSaving(true);
    setSaveError(null);
    const payload = {
      name: editing.name,
      type: editing.type,
      district_id: Number(editing.district_id),
      lat: editing.lat,
      lng: editing.lng,
      aliases: editing.aliases,
      is_active: editing.is_active,
    };
    try {
      if (editing.id) await finderPlacesService.update(restaurantId, editing.id, payload);
      else await finderPlacesService.create(restaurantId, payload);
      await load();
      setEditing(null);
    } catch (e) {
      setSaveError(e?.response?.data?.error || e.message || t('settings.places.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (lm) => {
    const ok = await confirm({
      title: t('settings.places.deactivate'),
      description: t('settings.places.confirmDeactivate', { name: lm.name }),
      confirmText: t('settings.places.deactivate'),
      destructive: true,
    });
    if (!ok) return;
    try {
      await finderPlacesService.deactivate(restaurantId, lm.id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || t('settings.places.saveFailed'));
    }
  };

  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return landmarks;
    return landmarks.filter((lm) =>
      [lm.name, lm.district, ...(lm.aliases || [])].some((s) => String(s || '').toLowerCase().includes(f)));
  }, [landmarks, filter]);

  if (!restaurantId) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          {t('settings.places.noRestaurant')}
        </div>
      </div>
    );
  }
  if (!canView) {
    return <AccessDenied perm="settings.view" what={t('settings.places.title')} />;
  }

  return (
    <div className={embedded ? 'w-full max-w-full' : 'p-6 w-full max-w-full'}>
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          {embedded ? (
            /* Inside Website Settings the page heading is already above; this is one of its sections. */
            <h2 className="text-xl font-semibold text-foreground">{t('settings.places.title')}</h2>
          ) : (
            <>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{t('settings.places.eyebrow')}</div>
              <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">{t('settings.places.title')}</h1>
            </>
          )}
        </div>
        {!editing && tab === 'landmarks' && (
          <div className="flex items-center gap-4 shrink-0">
            <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
              />
              {t('settings.places.showInactive')}
            </label>
            {canCreate && (
              <button
                type="button"
                onClick={openNew}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <Plus size={16} /> {t('settings.places.add')}
              </button>
            )}
          </div>
        )}
      </header>

      {!editing && (
        <>
          <p className="mb-4 max-w-3xl text-sm text-muted-foreground">{t('settings.places.intro')}</p>
          <div className="mb-6 flex gap-1 border-b border-border" role="tablist">
            {[['landmarks', t('settings.places.tab.landmarks')], ['popular', t('settings.places.tab.popular')]].map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                  tab === key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </>
      )}

      {!canEdit && (
        <div className="mb-4 rounded-md border border-warning/30 bg-warning-soft px-4 py-2.5 text-sm text-warning">
          <strong>{t('settings.readOnly')}</strong> {t('settings.places.readOnlyBody')}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {editing ? (
        <LandmarkEditor
          editing={editing}
          districts={districts}
          setField={setField}
          onClose={closeEditor}
          onSave={save}
          saving={saving}
          saveError={saveError}
          canEdit={canEdit}
        />
      ) : tab === 'popular' ? (
        <PopularTab restaurantId={restaurantId} canEdit={canEdit} />
      ) : loading ? (
        <div className="rounded-md border border-border bg-card px-4 py-8 text-center text-sm text-muted-foreground">
          {t('settings.places.loading')}
        </div>
      ) : (
        <LandmarkList
          rows={shown}
          total={landmarks.length}
          filter={filter}
          setFilter={setFilter}
          onOpen={openExisting}
          onDeactivate={deactivate}
          onAdd={openNew}
          canEdit={canEdit}
          canCreate={canCreate}
        />
      )}
    </div>
  );
}

/* --------------------------------- list --------------------------------- */

function LandmarkList({ rows, total, filter, setFilter, onOpen, onDeactivate, onAdd, canEdit, canCreate }) {
  const { t } = useT();
  if (!total) {
    return (
      <div className="rounded-lg border-2 border-dashed border-border bg-card px-6 py-12 text-center">
        <Hotel className="mx-auto h-10 w-10 text-muted-foreground" />
        <h3 className="mt-3 text-sm font-semibold text-foreground">{t('settings.places.empty.title')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{t('settings.places.empty.body')}</p>
        {canCreate && (
          <button
            type="button"
            onClick={onAdd}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus size={16} /> {t('settings.places.add')}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <input
          className={`${inputCls} pl-9`}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('settings.places.filterPh')}
          aria-label={t('settings.places.filterPh')}
        />
      </div>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5">{t('settings.places.col.name')}</th>
              <th className="px-4 py-2.5">{t('settings.places.col.type')}</th>
              <th className="px-4 py-2.5">{t('settings.places.col.district')}</th>
              <th className="px-4 py-2.5">{t('settings.places.col.coords')}</th>
              <th className="px-4 py-2.5">{t('settings.places.col.aliases')}</th>
              <th className="px-4 py-2.5">{t('settings.places.col.status')}</th>
              <th className="px-4 py-2.5"><span className="sr-only">{t('settings.places.col.actions')}</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((lm) => (
              <tr key={lm.id} className={lm.is_active ? '' : 'opacity-60'}>
                <td className="px-4 py-2.5 font-medium text-foreground">
                  <span className="inline-flex items-center gap-2">
                    <KindIcon kind={lm.type} className="h-4 w-4 text-muted-foreground" />
                    {lm.name}
                    {lm.popular_rank != null && (
                      <span title={t('settings.places.popularBadge', { rank: lm.popular_rank })} className="inline-flex items-center gap-0.5 rounded-full bg-warning-soft px-1.5 py-0.5 text-[10px] font-semibold text-warning">
                        <Star className="h-3 w-3" aria-hidden="true" /> {lm.popular_rank}
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-muted-foreground">{t(`settings.places.type.${lm.type}`)}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{lm.district}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{Number(lm.lat).toFixed(4)}, {Number(lm.lng).toFixed(4)}</td>
                <td className="px-4 py-2.5 text-muted-foreground max-w-xs truncate" title={(lm.aliases || []).join(', ')}>{(lm.aliases || []).join(', ') || '—'}</td>
                <td className="px-4 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${lm.is_active ? 'bg-success-soft text-success' : 'bg-muted text-muted-foreground'}`}>
                    {lm.is_active ? t('settings.places.active') : t('settings.places.inactive')}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  <button type="button" onClick={() => onOpen(lm)} className="text-sm font-medium text-primary hover:underline">
                    {canEdit ? t('settings.places.edit') : t('settings.places.view')}
                  </button>
                  {canEdit && lm.is_active && (
                    <button type="button" onClick={() => onDeactivate(lm)} className="ml-3 text-sm font-medium text-destructive hover:underline">
                      {t('settings.places.deactivate')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------------------------------- editor -------------------------------- */

function LandmarkEditor({ editing, districts, setField, onClose, onSave, saving, saveError, canEdit }) {
  const { t } = useT();
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="mb-6 flex items-center justify-between gap-4">
        <button type="button" onClick={onClose} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={16} /> {t('settings.places.back')}
        </button>
        <h2 className="text-lg font-semibold text-foreground">
          {editing.id ? editing.name || t('settings.places.editTitle') : t('settings.places.newTitle')}
        </h2>
        <div className="w-24" />
      </div>
      {saveError && (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2.5 text-sm text-destructive">{saveError}</div>
      )}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 max-w-3xl">
        <Field label={t('settings.places.field.name')} required>
          <input className={inputCls} value={editing.name} disabled={!canEdit} maxLength={120}
            onChange={(e) => setField('name', e.target.value)} placeholder={t('settings.places.field.namePh')} />
        </Field>
        <Field label={t('settings.places.field.type')} required>
          <select className={inputCls} value={editing.type} disabled={!canEdit} onChange={(e) => setField('type', e.target.value)}>
            <option value="hotel">{t('settings.places.type.hotel')}</option>
            <option value="landmark">{t('settings.places.type.landmark')}</option>
            <option value="airport">{t('settings.places.type.airport')}</option>
          </select>
        </Field>
        <Field label={t('settings.places.field.district')} required>
          <select className={inputCls} value={editing.district_id} disabled={!canEdit} onChange={(e) => setField('district_id', e.target.value)}>
            <option value="">{t('settings.places.field.districtPick')}</option>
            {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t('settings.places.field.lat')} required hint={t('settings.places.field.latHint')}>
            <input className={inputCls} type="number" step="0.000001" min="-90" max="90" inputMode="decimal" disabled={!canEdit}
              value={editing.lat} onChange={(e) => setField('lat', e.target.value)} placeholder="-20.4302" />
          </Field>
          <Field label={t('settings.places.field.lng')} required hint={t('settings.places.field.lngHint')}>
            <input className={inputCls} type="number" step="0.000001" min="-180" max="180" inputMode="decimal" disabled={!canEdit}
              value={editing.lng} onChange={(e) => setField('lng', e.target.value)} placeholder="57.6836" />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label={t('settings.places.field.aliases')} hint={t('settings.places.field.aliasesHint')}>
            <input className={inputCls} value={editing.aliases} disabled={!canEdit}
              onChange={(e) => setField('aliases', e.target.value)} placeholder={t('settings.places.field.aliasesPh')} />
          </Field>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-foreground cursor-pointer select-none">
          <input type="checkbox" checked={!!editing.is_active} disabled={!canEdit}
            onChange={(e) => setField('is_active', e.target.checked)}
            className="h-4 w-4 rounded border-border text-primary focus:ring-ring" />
          {t('settings.places.field.active')}
        </label>
      </div>
      {canEdit && (
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-input px-4 py-2 text-sm text-foreground hover:bg-muted">
            {t('settings.places.cancel')}
          </button>
          <button type="button" onClick={onSave} disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
            <Save size={16} /> {saving ? t('settings.places.saving') : t('settings.places.save')}
          </button>
        </div>
      )}
    </div>
  );
}

/* -------------------------------- popular -------------------------------- */

function PopularTab({ restaurantId, canEdit }) {
  const { t } = useT();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [limit, setLimit] = useState(6);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    finderPlacesService.popular(restaurantId)
      .then(({ items: rows, limit: n }) => { if (!cancelled) { setItems(rows); setLimit(n); } })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [restaurantId]);

  // Debounced lookup through the public typeahead — the same answer a guest gets.
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) { setMatches([]); return undefined; }
    setSearching(true);
    timer.current = setTimeout(() => {
      finderPlacesService.searchPlaces(q.trim())
        .then((rows) => setMatches(rows.filter((m) => !items.some((i) => i.ref === m.ref))))
        .catch(() => setMatches([]))
        .finally(() => setSearching(false));
    }, 180);
    return () => clearTimeout(timer.current);
  }, [q, items]);

  const mutate = (next) => { setItems(next); setDirty(true); };
  const add = (m) => {
    if (items.length >= limit) return;
    mutate([...items, m]);
    setQ('');
    setMatches([]);
  };
  const remove = (i) => mutate(items.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    mutate(next);
  };
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const { items: rows } = await finderPlacesService.setPopular(restaurantId, items.map((i) => i.ref));
      setItems(rows);
      setDirty(false);
      toast({ title: t('settings.places.popular.saved') });
    } catch (e) {
      setError(e?.response?.data?.error || e.message || t('settings.places.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="rounded-md border border-border bg-card px-4 py-8 text-center text-sm text-muted-foreground">{t('settings.places.loading')}</div>;
  }
  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-start gap-2 rounded-md border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
        <Info size={16} className="mt-0.5 shrink-0" />
        <span>{t('settings.places.popular.intro', { n: limit })}</span>
      </div>
      {error && <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2.5 text-sm text-destructive">{error}</div>}

      <ol className="divide-y divide-border rounded-lg border border-border bg-card">
        {items.length === 0 && (
          <li className="px-4 py-6 text-center text-sm text-muted-foreground">{t('settings.places.popular.empty')}</li>
        )}
        {items.map((it, i) => (
          <li key={it.ref} className="flex items-center gap-3 px-4 py-2.5">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">{i + 1}</span>
            <KindIcon kind={it.kind} className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-foreground">{it.label}</span>
              <span className="block truncate text-xs text-muted-foreground">{it.sublabel}</span>
            </span>
            {canEdit && (
              <span className="flex shrink-0 items-center gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label={t('settings.places.popular.up')}
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30"><ArrowUp size={16} /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label={t('settings.places.popular.down')}
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30"><ArrowDown size={16} /></button>
                <button type="button" onClick={() => remove(i)} aria-label={t('settings.places.popular.remove')}
                  className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"><X size={16} /></button>
              </span>
            )}
          </li>
        ))}
      </ol>

      {canEdit && (
        <div className="space-y-2">
          <div className="text-xs text-muted-foreground">{t('settings.places.popular.count', { n: items.length, limit })}</div>
          {items.length < limit ? (
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <input
                className={`${inputCls} pl-9`}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('settings.places.popular.searchPh')}
                aria-label={t('settings.places.popular.searchPh')}
              />
              {q.trim() && (
                <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border border-border bg-card shadow-lg">
                  {searching && matches.length === 0 && (
                    <li className="px-3 py-2 text-sm text-muted-foreground">{t('settings.places.loading')}</li>
                  )}
                  {!searching && matches.length === 0 && (
                    <li className="px-3 py-2 text-sm text-muted-foreground">{t('settings.places.popular.noMatches')}</li>
                  )}
                  {matches.map((m) => (
                    <li key={m.ref}>
                      <button type="button" onClick={() => add(m)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted">
                        <KindIcon kind={m.kind} className="h-4 w-4 text-muted-foreground" />
                        <span className="flex-1 truncate text-foreground">{m.label}</span>
                        <span className="truncate text-xs text-muted-foreground">{m.sublabel}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">{t('settings.places.popular.full', { limit })}</div>
          )}
          <div className="flex justify-end">
            <button type="button" onClick={save} disabled={!dirty || saving}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              <Save size={16} /> {saving ? t('settings.places.saving') : t('settings.places.popular.save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
